import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { NotificationType } from 'src/app/enum/notification-type.enum';
import { Buttons } from 'src/app/home/table/Buttons';
import { TableColumn } from 'src/app/home/table/TableColumn';
import { RntFeedModel } from 'src/app/models/commission/rnt-feed.model';
import { CustomHttpRespone } from 'src/app/models/customhttpresponse/custom-http-response';
import { CommissionService } from 'src/app/service/commission.service';
import { ExportExcelService } from 'src/app/service/export-excel.service';
import { KomFeedService } from 'src/app/service/komfeed.service';
import { NotificationService } from 'src/app/service/notification.service';

@Component({
  selector: 'app-rnt-adjustments',
  templateUrl: './rnt-adjustments.component.html',
  styleUrls: ['./rnt-adjustments.component.css']
})
export class RntAdjustmentsComponent implements OnInit {
  /** Rows currently shown in the table (one UI page). */
  feedResultsData: RntFeedModel[] = [];
  columnsData: any;
  columnsExcelData: any;
  ordersTableColumns: TableColumn[];
  ordersTableExcelHeaderColumns: TableColumn[];
  dataForExcel = [];
  buttons: Buttons[] = [];
  actions: any;
  resHeadder: string = 'RTN Feed Results';

  totalCount = 0;
  pageIndex = 0;
  pageSize = 10;
  paginationSizes = [10, 20, 30, 40, 50];
  loadingPage = false;

  constructor(
    private komFeedService: KomFeedService,
    private commissionService: CommissionService,
    private notificationService: NotificationService,
    private httpService: HttpClient,
    private router: Router,
    private exportExcelService: ExportExcelService
  ) { }

  ngOnInit(): void {
    this.initializeColumns();
    this.initializeExcelHeaders();
    this.bootstrapFromSearch();
  }

  private get chunkSize(): number {
    return this.commissionService.rntFeedChunkSize;
  }

  private bootstrapFromSearch() {
    this.totalCount = this.commissionService.rntFeedTotalCount || 0;
    const cached0 = this.commissionService.rntFeedChunkCache?.[0];
    if (cached0?.length) {
      this.ensureRangeLoaded(0, Math.min(this.pageSize, this.totalCount || cached0.length));
      return;
    }
    if (this.commissionService.rntFeedResultsData?.length > 0) {
      this.commissionService.rntFeedChunkCache = { 0: this.commissionService.rntFeedResultsData };
      if (!this.totalCount) {
        this.totalCount = this.commissionService.rntFeedResultsData.length;
        this.commissionService.rntFeedTotalCount = this.totalCount;
      }
      this.ensureRangeLoaded(0, Math.min(this.pageSize, this.totalCount));
      return;
    }
    this.fetchChunk(0, () => {
      if (!this.totalCount) {
        this.notificationService.sendNotification(NotificationType.WARNING, 'No Records Found');
        return;
      }
      this.refreshVisibleRows();
    });
  }

  onPage(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    const start = this.pageIndex * this.pageSize;
    const end = Math.min(start + this.pageSize, this.totalCount);
    this.ensureRangeLoaded(start, end);
  }

  private ensureRangeLoaded(start: number, end: number) {
    if (end <= start) {
      this.feedResultsData = [];
      return;
    }
    const firstChunk = Math.floor(start / this.chunkSize);
    const lastChunk = Math.floor((end - 1) / this.chunkSize);
    const missing: number[] = [];
    for (let c = firstChunk; c <= lastChunk; c++) {
      if (!this.commissionService.rntFeedChunkCache[c]) {
        missing.push(c);
      }
    }
    if (!missing.length) {
      this.refreshVisibleRows();
      return;
    }
    this.fetchChunk(missing[0], () => this.ensureRangeLoaded(start, end));
  }

  private fetchChunk(chunkIndex: number, done: () => void) {
    const searchModel = {
      ...this.commissionService.rntFeedModel,
      limit: this.chunkSize,
      offset: chunkIndex * this.chunkSize
    };
    this.loadingPage = true;
    this.komFeedService.getRntFeedSearchResults(searchModel).subscribe((page) => {
      const rows = page?.rows || [];
      this.totalCount = page?.total ?? this.totalCount;
      this.commissionService.rntFeedTotalCount = this.totalCount;
      this.commissionService.rntFeedChunkCache[chunkIndex] = rows;
      if (chunkIndex === 0) {
        this.commissionService.rntFeedResultsData = rows;
      }
      this.loadingPage = false;
      done();
    }, (errorResponse: HttpErrorResponse) => {
      this.loadingPage = false;
      var msg: any;
      msg = errorResponse;
      this.notificationService.sendNotification(NotificationType.ERROR, msg);
    });
  }

  private refreshVisibleRows() {
    const start = this.pageIndex * this.pageSize;
    const end = Math.min(start + this.pageSize, this.totalCount);
    const rows: RntFeedModel[] = [];
    for (let i = start; i < end; i++) {
      const chunkIndex = Math.floor(i / this.chunkSize);
      const chunk = this.commissionService.rntFeedChunkCache[chunkIndex];
      if (!chunk) {
        break;
      }
      const row = chunk[i % this.chunkSize];
      if (row) {
        rows.push(row);
      }
    }
    this.feedResultsData = rows;
    this.commissionService.rntFeedResultsData = this.flattenCachedRows();
  }

  private flattenCachedRows(): RntFeedModel[] {
    const keys = Object.keys(this.commissionService.rntFeedChunkCache)
      .map(k => Number(k))
      .sort((a, b) => a - b);
    const all: RntFeedModel[] = [];
    for (const key of keys) {
      all.push(...(this.commissionService.rntFeedChunkCache[key] || []));
    }
    return all;
  }

  initializeColumns(): void {
    this.httpService.get('../../../assets/json/tableColumns.json').subscribe(
      data => {
        this.columnsData = data;
        this.ordersTableColumns = this.columnsData.RntFeedResults;
      }, (errorResponse: HttpErrorResponse) => {
        var msg: any;
        msg = errorResponse;
        this.notificationService.sendNotification(NotificationType.ERROR, msg);
      }
    );
    this.actions = [
      {
        name: 'Update',
        dataKey: 'far fa-edit success f-25',
        position: 'left',
        isSortable: false,
        class: 'badge bg-success'
      },
      {
        name: 'Delete',
        dataKey: 'far fa-trash-alt danger f-25',
        position: 'left',
        isSortable: false,
        class: 'badge bg-danger'
      }
    ];
    this.buttons = [
      {
        name: 'Back',
        pagenavigation: 'adjustments-feed'
      }
    ];
  }

  initializeExcelHeaders(): void {
    this.httpService.get('../../../assets/json/excelHeaders.json').subscribe(
      data => {
        this.columnsExcelData = data;
        this.ordersTableExcelHeaderColumns = this.columnsExcelData.RntFeedResults;
      }, (errorResponse: HttpErrorResponse) => {
        var msg: any;
        msg = errorResponse;
        this.notificationService.sendNotification(NotificationType.ERROR, msg);
      }
    );
  }

  innerRowAction1(row: any) {
    if (row.value === 'Update') {
      this.commissionService.editRntFeedModel = { ...row.row };
      this.router.navigate(['/edit-rntFeed']);
    } else if (row.value === 'Delete') {
      if (!row.row?.ret_id) {
        this.notificationService.sendNotification(NotificationType.WARNING, 'Missing Ret ID');
        return;
      }
      if (!confirm('Delete retention record ' + row.row.ret_id + '?')) {
        return;
      }
      this.komFeedService.deleteRntFeedById(row.row.ret_id).subscribe(
        (response: CustomHttpRespone) => {
          this.notificationService.sendNotification(NotificationType.SUCCESS, response.message);
          this.removeFromCache(row.row.ret_id);
          this.totalCount = Math.max(0, this.totalCount - 1);
          this.commissionService.rntFeedTotalCount = this.totalCount;
          this.refreshVisibleRows();
        },
        (errorResponse: HttpErrorResponse) => {
          var msg: any;
          msg = errorResponse?.error?.message || errorResponse;
          this.notificationService.sendNotification(NotificationType.ERROR, msg);
        }
      );
    }
  }

  private removeFromCache(retId: number) {
    const cache = this.commissionService.rntFeedChunkCache;
    Object.keys(cache).forEach(key => {
      cache[Number(key)] = (cache[Number(key)] || []).filter(r => r.ret_id !== retId);
    });
  }

  exportExcel() {
    const exportData = JSON.parse(JSON.stringify(this.flattenCachedRows()));
    this.dataForExcel = [];
    exportData.forEach((row: any) => {
      this.dataForExcel.push(Object.values(row));
    });
    const reportData = {
      title: 'RTN Feed Data',
      data: this.dataForExcel,
      headers: Object.keys(this.ordersTableExcelHeaderColumns[0] || {})
    };
    this.exportExcelService.exportExcel(reportData);
  }

  redirect(url: string) {
    this.router.navigate(['/' + url]);
  }
}
